import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  CircularProgress,
  Divider,
  Chip,
  Paper,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Snackbar,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  PlayArrow as TestIcon,
  Save as SaveIcon,
  Publish as ReleaseIcon,
  Undo as UndoIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import * as Diff from 'diff';
import api from '../api/axios';
import ExtractionOutputRenderer from './ExtractionOutputRenderer';
import ChainOfThought from './ChainOfThought';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ResponseMetadata {
  model?: string;
  reasoning_effort?: string;
  web_search_used?: boolean;
  web_searches?: Array<{ query?: string; status?: string }>;
  citations?: Array<{ type?: string; title?: string; url?: string }>;
  reasoning?: string[];
  usage?: { input_tokens?: number; output_tokens?: number; total_tokens?: number };
}

interface TestRunInfo {
  testRunId: string;
  versionId: string;
  version: string;
  entityType: string;
  entityTypeLabel: string;
}

interface PromptDebugModalProps {
  open: boolean;
  onClose: () => void;
  entityType: string;
  entityTypeLabel: string;
  currentVersion: string;
  promptText: string;
  pressReleaseId: string;
  pressReleaseTitle: string;
  pressReleaseBody: string;
  currentOutput: any;
  responseMetadata?: ResponseMetadata | null;
  onVersionCreated?: (newVersionId: string) => void;
  onReleaseStarted?: (testRunInfo: TestRunInfo) => void;
}

export default function PromptDebugModal({
  open,
  onClose,
  entityType,
  entityTypeLabel,
  currentVersion,
  promptText: initialPromptText,
  pressReleaseId,
  pressReleaseTitle,
  pressReleaseBody,
  currentOutput,
  responseMetadata,
  onVersionCreated,
  onReleaseStarted,
}: PromptDebugModalProps) {
  // State
  const [promptText, setPromptText] = useState(initialPromptText);
  const [originalPromptText] = useState(initialPromptText);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [testOutput, setTestOutput] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedVersionId, setSavedVersionId] = useState<string | null>(null);
  const [savedVersionInfo, setSavedVersionInfo] = useState<{ version: string } | null>(null);
  const [previousResponseId, setPreviousResponseId] = useState<string | null>(null);
  const [showCopied, setShowCopied] = useState(false);
  const [promptWidth, setPromptWidth] = useState(35); // percentage
  const [isDragging, setIsDragging] = useState(false);
  const [outputTab, setOutputTab] = useState(0); // 0 = current, 1 = test
  const [previousPromptBeforeApply, setPreviousPromptBeforeApply] = useState<string | null>(null);
  const [showApplied, setShowApplied] = useState(false);
  const [diffPreview, setDiffPreview] = useState<{ newText: string; changes: Diff.Change[] } | null>(null);
  const [diffTab, setDiffTab] = useState(0); // 0 = diff view, 1 = edit
  const [testStatus, setTestStatus] = useState<string | null>(null);
  const [showTestComplete, setShowTestComplete] = useState(false);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const promptEditorRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setPromptText(initialPromptText);
      setMessages([]);
      setInputValue('');
      setTestOutput(null);
      setError(null);
      setSavedVersionId(null);
      setSavedVersionInfo(null);
      setPreviousResponseId(null);
      setShowCopied(false);
      setPromptWidth(35);
      setOutputTab(0);
      setPreviousPromptBeforeApply(null);
      setShowApplied(false);
      setDiffPreview(null);
      setTestStatus(null);
      setShowTestComplete(false);
    }
  }, [open, initialPromptText]);

  // Auto-switch to test tab when test output becomes available
  useEffect(() => {
    if (testOutput) {
      setOutputTab(1);
    }
  }, [testOutput]);

  // Handle resizable divider drag
  const handleMouseDown = () => {
    setIsDragging(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;
      // Constrain between 20% and 60%
      setPromptWidth(Math.min(60, Math.max(20, newWidth)));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Copy prompt to clipboard
  const handleCopyPrompt = () => {
    navigator.clipboard.writeText(promptText);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  // Scroll to bottom of chat when messages change
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const hasChanges = promptText !== originalPromptText;

  // Send debug message using streaming Responses API with previous_response_id for multi-turn
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = { role: 'user', content: inputValue.trim() };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setError(null);

    // Track whether we've added the assistant message yet
    let assistantMessageAdded = false;

    try {
      // Use fetch for streaming (can't use axios for SSE)
      const accessToken = localStorage.getItem('access');
      const accountId = localStorage.getItem('current_account_id');

      const response = await fetch('/api/schemes/evaluate/debug/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
          ...(accountId && { 'X-Account-ID': accountId }),
        },
        body: JSON.stringify({
          message: userMessage.content,
          previous_response_id: previousResponseId,
          context: {
            prompt_text: promptText,
            press_release_title: pressReleaseTitle,
            press_release_body: pressReleaseBody,
            extraction_output: JSON.stringify(currentOutput, null, 2),
            entity_type: entityType,
            response_metadata: responseMetadata ? {
              reasoning: responseMetadata.reasoning,
              web_searches: responseMetadata.web_searches,
              citations: responseMetadata.citations,
            } : null,
            // Include test output if available for comparison
            test_output: testOutput ? JSON.stringify(testOutput.output, null, 2) : null,
            test_metadata: testOutput?.metadata || null,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              const event = JSON.parse(data);

              if (event.type === 'delta') {
                // On first delta, add the assistant message
                if (!assistantMessageAdded) {
                  setMessages((prev) => [...prev, { role: 'assistant', content: event.content }]);
                  assistantMessageAdded = true;
                } else {
                  // Update the assistant message with new content (create new objects for React)
                  setMessages((prev) => {
                    const updated = [...prev];
                    const lastIndex = updated.length - 1;
                    const lastMsg = updated[lastIndex];
                    if (lastMsg && lastMsg.role === 'assistant') {
                      updated[lastIndex] = {
                        ...lastMsg,
                        content: lastMsg.content + event.content,
                      };
                    }
                    return updated;
                  });
                }
              } else if (event.type === 'done') {
                // Store response_id for multi-turn conversation
                if (event.response_id) {
                  setPreviousResponseId(event.response_id);
                }
              } else if (event.type === 'error') {
                setError(event.error || 'Stream error');
              }
            } catch (parseErr) {
              // Ignore parse errors for incomplete JSON
            }
          }
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to get response');
    } finally {
      setIsLoading(false);
    }
  };

  // Show diff preview before applying suggestion
  const handleApplySuggestion = (codeBlock: string) => {
    // Extract the code from the code block (strip ``` markers)
    const code = codeBlock.replace(/```\w*\n?/g, '').replace(/```$/g, '').trim();

    if (code) {
      // Compute diff and show preview
      const changes = Diff.diffLines(promptText, code);
      setDiffPreview({ newText: code, changes });
      setDiffTab(0); // Start on diff view
    }
  };

  // Update the diff preview when user edits in the edit tab
  const handleDiffTextChange = (newText: string) => {
    const changes = Diff.diffLines(promptText, newText);
    setDiffPreview({ newText, changes });
  };

  // Confirm and apply the previewed changes
  const handleConfirmApply = () => {
    if (diffPreview) {
      // Save current prompt for potential undo
      setPreviousPromptBeforeApply(promptText);
      // Replace entire prompt with the suggestion
      setPromptText(diffPreview.newText);
      // Close diff preview
      setDiffPreview(null);
      // Show applied indicator
      setShowApplied(true);
      setTimeout(() => setShowApplied(false), 3000);
      // Scroll to top of editor to see the changes
      if (promptEditorRef.current) {
        promptEditorRef.current.scrollTop = 0;
      }
    }
  };

  // Cancel the diff preview
  const handleCancelApply = () => {
    setDiffPreview(null);
  };

  // Undo the last applied suggestion
  const handleUndoApply = () => {
    if (previousPromptBeforeApply !== null) {
      setPromptText(previousPromptBeforeApply);
      setPreviousPromptBeforeApply(null);
      setShowApplied(false);
    }
  };

  // Test modified prompt via Celery with polling
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleTestPrompt = async () => {
    setIsTesting(true);
    setError(null);
    setTestOutput(null);
    setTestStatus('Starting test...');

    try {
      // Start the test run
      const response = await api.post('/schemes/evaluate/test-prompt/', {
        prompt_text: promptText,
        press_release_id: pressReleaseId,
        entity_type: entityType,
        model: 'gpt-5.2',
      });

      const testRunId = response.data.test_run_id;
      setTestStatus('Running extraction (this may take 1-2 minutes)...');

      // Poll for results
      pollIntervalRef.current = setInterval(async () => {
        try {
          const statusRes = await api.get(`/schemes/evaluate/test-prompt/${testRunId}/`);
          const data = statusRes.data;

          if (data.status === 'completed' || data.status === 'failed') {
            // Stop polling
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }

            if (data.status === 'completed' && data.extraction) {
              setTestOutput({
                output: data.extraction.output,
                latency_ms: data.extraction.latency_ms,
                tokens_used: data.extraction.tokens_used,
                metadata: data.extraction.metadata,
              });
              setShowTestComplete(true);
            } else if (data.status === 'failed') {
              setError(data.extraction?.error_message || 'Test failed');
            }

            setIsTesting(false);
            setTestStatus(null);
          }
        } catch (pollErr) {
          console.error('Polling error:', pollErr);
        }
      }, 2000); // Poll every 2 seconds

    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to start test');
      setIsTesting(false);
      setTestStatus(null);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Save as new version
  const handleSaveVersion = async () => {
    setIsSaving(true);
    setError(null);

    try {
      const response = await api.post('/schemes/prompts/versions/', {
        entity_type_slug: entityType,
        prompt_text: promptText,
        model: 'gpt-5.2',
        notes: `Created from debug modal on ${new Date().toLocaleDateString()}`,
      });

      setSavedVersionId(response.data.id);
      setSavedVersionInfo({ version: response.data.version });
      onVersionCreated?.(response.data.id);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save version');
    } finally {
      setIsSaving(false);
    }
  };

  // Release and run on all test press releases
  const handleReleaseAndRun = async () => {
    if (!savedVersionId || !savedVersionInfo) return;

    setIsSaving(true);
    setError(null);

    try {
      // Release the version
      await api.post(`/schemes/prompts/versions/${savedVersionId}/release/`);

      // Start extraction run in release mode (creates permanent ExtractionRun records)
      const testResponse = await api.post(`/schemes/prompts/versions/${savedVersionId}/test/`, {
        release_mode: true
      });

      // Notify parent about the test run so it can show progress
      if (onReleaseStarted) {
        onReleaseStarted({
          testRunId: testResponse.data.id,
          versionId: savedVersionId,
          version: savedVersionInfo.version,
          entityType,
          entityTypeLabel,
        });
      }

      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to release');
    } finally {
      setIsSaving(false);
    }
  };

  // Render message with markdown support
  const renderMessage = (message: Message, index: number) => {
    const isAssistant = message.role === 'assistant';
    const content = message.content;

    // Extract code blocks for "Apply to Prompt" functionality
    const codeBlocks = content.match(/```[\s\S]*?```/g) || [];

    return (
      <Box
        key={index}
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: isAssistant ? 'flex-start' : 'flex-end',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
          {isAssistant ? 'Eliot' : 'You'}
        </Typography>
        <Paper
          sx={{
            p: 1.5,
            maxWidth: '90%',
            bgcolor: isAssistant ? 'grey.100' : 'primary.50',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              fontSize: '0.85rem',
              lineHeight: 1.6,
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
              '& ul, & ol': { m: 0, mb: 1, pl: 2.5 },
              '& li': { mb: 0.5 },
              '& code': {
                bgcolor: 'grey.200',
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                fontFamily: 'monospace',
                fontSize: '0.8em',
                wordBreak: 'break-all',
              },
              '& pre': {
                bgcolor: 'grey.900',
                color: 'grey.100',
                p: 1.5,
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: 200,
                maxWidth: '100%',
                my: 1,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                '& code': {
                  bgcolor: 'transparent',
                  p: 0,
                  fontSize: '0.75rem',
                  wordBreak: 'break-all',
                },
              },
              '& strong': { fontWeight: 600 },
              '& h1, & h2, & h3, & h4': { fontWeight: 600, mt: 1.5, mb: 0.5 },
              '& h1': { fontSize: '1.1rem' },
              '& h2': { fontSize: '1rem' },
              '& h3': { fontSize: '0.95rem' },
            }}
          >
            <ReactMarkdown>{content}</ReactMarkdown>
          </Box>
          {/* Apply to Prompt buttons for code blocks */}
          {isAssistant && codeBlocks.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {codeBlocks.map((block, i) => (
                <Button
                  key={i}
                  size="small"
                  variant="outlined"
                  onClick={() => handleApplySuggestion(block)}
                  sx={{ fontSize: '0.7rem' }}
                >
                  View {codeBlocks.length > 1 ? `Block ${i + 1} ` : 'Prompt '}Changes
                </Button>
              ))}
            </Box>
          )}
        </Paper>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '95vw',
          height: '90vh',
          maxWidth: 1600,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            Debug: {entityTypeLabel}
          </Typography>
          <Chip label={`Editing: ${currentVersion}`} size="small" variant="outlined" />
          {hasChanges && !savedVersionId && <Chip label="Unsaved changes" size="small" color="warning" />}
          {savedVersionInfo && (
            <Chip
              label={`✓ Saved as ${savedVersionInfo.version} (Draft)`}
              size="small"
              color="success"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent ref={containerRef} sx={{ p: 0, display: 'flex', height: 'calc(100% - 64px - 64px)', bgcolor: 'grey.100', userSelect: isDragging ? 'none' : 'auto' }}>
        {/* Left Panel - Prompt Editor (resizable) */}
        <Box sx={{ flex: `0 0 ${promptWidth}%`, minWidth: 200, maxWidth: '60%', display: 'flex', flexDirection: 'column', bgcolor: 'grey.50' }}>
          <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Prompt (editable)</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={handleCopyPrompt}
                title="Copy prompt"
                sx={{ p: 0.5 }}
              >
                <CopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
              {hasChanges && (
                <Button
                  size="small"
                  startIcon={<UndoIcon sx={{ fontSize: 14 }} />}
                  onClick={() => setPromptText(originalPromptText)}
                  sx={{ fontSize: '0.7rem', py: 0.25, minWidth: 'auto' }}
                >
                  Revert
                </Button>
              )}
            </Box>
          </Box>
          {showCopied && (
            <Box sx={{ bgcolor: 'success.main', color: 'white', px: 1, py: 0.25, fontSize: '0.7rem', textAlign: 'center' }}>
              Copied!
            </Box>
          )}
          <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden', m: 1, borderRadius: 1, border: '2px solid', borderColor: 'primary.light' }}>
            <textarea
              ref={promptEditorRef}
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              style={{
                width: '100%',
                height: '100%',
                padding: '12px',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                lineHeight: 1.5,
                border: 'none',
                outline: 'none',
                resize: 'none',
                backgroundColor: '#fff',
              }}
            />
            {/* Applied overlay with undo button */}
            {showApplied && previousPromptBeforeApply !== null && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'success.main',
                  color: 'success.contrastText',
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  boxShadow: 2,
                }}
              >
                <span>Prompt replaced</span>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleUndoApply}
                  sx={{
                    color: 'inherit',
                    borderColor: 'rgba(255,255,255,0.5)',
                    fontSize: '0.65rem',
                    py: 0,
                    px: 0.75,
                    minWidth: 'auto',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Undo
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {/* Resizable Divider */}
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            width: 6,
            cursor: 'col-resize',
            bgcolor: isDragging ? 'primary.main' : 'grey.300',
            '&:hover': { bgcolor: 'primary.light' },
            transition: 'background-color 0.15s',
            flexShrink: 0,
          }}
        />

        {/* Right Panel - Output & Chat */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
          {/* Collapsible Output Section */}
          <Accordion defaultExpanded={true} disableGutters sx={{ boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'grey.50', minHeight: 36, px: 1, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Output</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0, display: 'flex', flexDirection: 'column', maxHeight: 350 }}>
              {/* Tabs for Current vs Test output */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
                <Tabs
                  value={outputTab}
                  onChange={(_, v) => setOutputTab(v)}
                  sx={{
                    minHeight: 32,
                    '& .MuiTab-root': { minHeight: 32, py: 0.5, fontSize: '0.75rem', textTransform: 'none' },
                  }}
                >
                  <Tab label={`Current (${currentVersion})`} />
                  <Tab
                    label={testOutput ? '✓ Test Result' : 'Test Result (run test first)'}
                    disabled={!testOutput}
                    sx={testOutput ? {
                      bgcolor: 'success.50',
                      color: 'success.dark',
                      fontWeight: 600,
                      borderRadius: '4px 4px 0 0',
                      '&.Mui-selected': {
                        bgcolor: 'success.100',
                        color: 'success.dark',
                      },
                    } : {}}
                  />
                </Tabs>
              </Box>
              {/* Tab Content */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
                {outputTab === 0 && (
                  <Box>
                    <ExtractionOutputRenderer
                      entityType={entityType}
                      output={currentOutput}
                      maxHeight={180}
                    />
                    {/* Metadata for current output */}
                    {responseMetadata && (
                      <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                        {/* Chain of Thought */}
                        {responseMetadata.reasoning && responseMetadata.reasoning.length > 0 && (
                          <Box sx={{ mb: 1 }}>
                            <ChainOfThought steps={responseMetadata.reasoning} defaultExpanded={false} />
                          </Box>
                        )}
                        {/* Web Searches */}
                        {responseMetadata.web_search_used && (
                          <Accordion
                            defaultExpanded={false}
                            disableGutters
                            sx={{ boxShadow: 'none', '&:before': { display: 'none' }, bgcolor: 'transparent', m: '0 !important' }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
                              sx={{ minHeight: '24px !important', p: 0, '& .MuiAccordionSummary-content': { m: '0 !important', alignItems: 'center', gap: 0.5 } }}
                            >
                              <SearchIcon sx={{ fontSize: 14 }} color="info" />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                Web Search {responseMetadata.web_searches?.length ? `(${responseMetadata.web_searches.length})` : ''}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0, pl: 2, m: 0 }}>
                              {responseMetadata.web_searches?.map((search, idx) => (
                                <Typography key={idx} sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                  • {search.query}
                                </Typography>
                              ))}
                            </AccordionDetails>
                          </Accordion>
                        )}
                        {/* Citations */}
                        {responseMetadata.citations && responseMetadata.citations.length > 0 && (
                          <Accordion
                            defaultExpanded={false}
                            disableGutters
                            sx={{ boxShadow: 'none', '&:before': { display: 'none' }, bgcolor: 'transparent', m: '0 !important' }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
                              sx={{ minHeight: '24px !important', p: 0, '& .MuiAccordionSummary-content': { m: '0 !important', alignItems: 'center', gap: 0.5 } }}
                            >
                              <LinkIcon sx={{ fontSize: 14 }} color="secondary" />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                Citations ({responseMetadata.citations.filter((c, i, arr) => arr.findIndex(x => x.url === c.url) === i).length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0, pl: 2, m: 0 }}>
                              {responseMetadata.citations
                                .filter((c, i, arr) => arr.findIndex(x => x.url === c.url) === i)
                                .map((citation, idx) => (
                                  <Typography
                                    key={idx}
                                    component="a"
                                    href={citation.url || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ display: 'block', fontSize: '0.65rem', color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                  >
                                    • {citation.title || citation.url}
                                  </Typography>
                                ))}
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
                {outputTab === 1 && testOutput && (
                  <Box>
                    <Box sx={{ border: '2px solid', borderColor: 'success.main', borderRadius: 1, bgcolor: 'success.50', p: 1, mb: 1 }}>
                      <ExtractionOutputRenderer
                        entityType={entityType}
                        output={testOutput.output}
                        maxHeight={150}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {testOutput.latency_ms}ms | {testOutput.tokens_used} tokens
                    </Typography>
                    {/* Metadata for test output */}
                    {testOutput.metadata && (
                      <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                        {/* Chain of Thought */}
                        {testOutput.metadata.reasoning && testOutput.metadata.reasoning.length > 0 && (
                          <Box sx={{ mb: 1 }}>
                            <ChainOfThought steps={testOutput.metadata.reasoning} defaultExpanded={false} />
                          </Box>
                        )}
                        {/* Web Searches */}
                        {testOutput.metadata.web_search_used && (
                          <Accordion
                            defaultExpanded={false}
                            disableGutters
                            sx={{ boxShadow: 'none', '&:before': { display: 'none' }, bgcolor: 'transparent', m: '0 !important' }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
                              sx={{ minHeight: '24px !important', p: 0, '& .MuiAccordionSummary-content': { m: '0 !important', alignItems: 'center', gap: 0.5 } }}
                            >
                              <SearchIcon sx={{ fontSize: 14 }} color="info" />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                Web Search {testOutput.metadata.web_searches?.length ? `(${testOutput.metadata.web_searches.length})` : ''}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0, pl: 2, m: 0 }}>
                              {testOutput.metadata.web_searches?.map((search: { query?: string }, idx: number) => (
                                <Typography key={idx} sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                  • {search.query}
                                </Typography>
                              ))}
                            </AccordionDetails>
                          </Accordion>
                        )}
                        {/* Citations */}
                        {testOutput.metadata.citations && testOutput.metadata.citations.length > 0 && (
                          <Accordion
                            defaultExpanded={false}
                            disableGutters
                            sx={{ boxShadow: 'none', '&:before': { display: 'none' }, bgcolor: 'transparent', m: '0 !important' }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
                              sx={{ minHeight: '24px !important', p: 0, '& .MuiAccordionSummary-content': { m: '0 !important', alignItems: 'center', gap: 0.5 } }}
                            >
                              <LinkIcon sx={{ fontSize: 14 }} color="secondary" />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                Citations ({testOutput.metadata.citations.filter((c: { url?: string }, i: number, arr: { url?: string }[]) => arr.findIndex(x => x.url === c.url) === i).length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0, pl: 2, m: 0 }}>
                              {testOutput.metadata.citations
                                .filter((c: { url?: string }, i: number, arr: { url?: string }[]) => arr.findIndex(x => x.url === c.url) === i)
                                .map((citation: { url?: string; title?: string }, idx: number) => (
                                  <Typography
                                    key={idx}
                                    component="a"
                                    href={citation.url || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ display: 'block', fontSize: '0.65rem', color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                  >
                                    • {citation.title || citation.url}
                                  </Typography>
                                ))}
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Chat - takes remaining space */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ px: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', minHeight: 36, display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Eliot Debugger</Typography>
            </Box>

            {/* Messages */}
            <Box sx={{ flex: 1, overflow: 'auto', overflowX: 'hidden', p: 1.5 }}>
              {messages.length === 0 && !isLoading && (
                <Typography color="text.secondary" sx={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                  Ask questions like "Why didn't you identify X?" or "How would you fix this?"
                </Typography>
              )}
              {messages.map((msg, i) => renderMessage(msg, i))}
              {isLoading && (messages.length === 0 || messages[messages.length - 1]?.role === 'user') && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">
                    Eliot is thinking...
                  </Typography>
                </Box>
              )}
              <div ref={chatEndRef} />
            </Box>

            {/* Input */}
            <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
              {error && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={1}
                  maxRows={4}
                  placeholder="Ask Eliot about the extraction..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                  disabled={isLoading}
                />
                <IconButton onClick={handleSendMessage} disabled={isLoading || !inputValue.trim()} color="primary">
                  <SendIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* Bottom Actions */}
      <Divider />
      <Box sx={{ p: 1.5, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
        {testStatus && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
            {testStatus}
          </Typography>
        )}
        <Button
          variant="outlined"
          startIcon={isTesting ? <CircularProgress size={16} /> : <TestIcon />}
          onClick={handleTestPrompt}
          disabled={isTesting || !hasChanges}
        >
          {isTesting ? 'Testing...' : 'Test on This Press Release'}
        </Button>
        <Button
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={handleSaveVersion}
          disabled={isSaving || !hasChanges || !!savedVersionId}
        >
          Save as New Version
        </Button>
        {savedVersionId && (
          <Button
            variant="contained"
            color="success"
            startIcon={isSaving ? <CircularProgress size={16} /> : <ReleaseIcon />}
            onClick={handleReleaseAndRun}
            disabled={isSaving}
          >
            Release & Run on All
          </Button>
        )}
      </Box>

      {/* Diff Preview Dialog */}
      <Dialog
        open={!!diffPreview}
        onClose={handleCancelApply}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '80vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">Review Changes</Typography>
            {diffPreview && (() => {
              const added = diffPreview.changes.filter(c => c.added).reduce((sum, c) => sum + c.value.split('\n').length - 1, 0);
              const removed = diffPreview.changes.filter(c => c.removed).reduce((sum, c) => sum + c.value.split('\n').length - 1, 0);
              return (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {added > 0 && <Chip label={`+${added} lines`} size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />}
                  {removed > 0 && <Chip label={`-${removed} lines`} size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />}
                </Box>
              );
            })()}
          </Box>
          <IconButton onClick={handleCancelApply} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        {/* Tabs for Diff View vs Edit */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs
            value={diffTab}
            onChange={(_, v) => setDiffTab(v)}
            sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.8rem', textTransform: 'none' } }}
          >
            <Tab label="Diff View" />
            <Tab label="Edit" />
          </Tabs>
        </Box>
        <DialogContent sx={{ p: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          {diffTab === 0 ? (
            /* Diff View */
            <Box sx={{ fontFamily: 'monospace', fontSize: '0.8rem', lineHeight: 1.7 }}>
              {diffPreview?.changes.map((part, index) => {
                const lines = part.value.split('\n');
                // Remove trailing empty line from split
                if (lines[lines.length - 1] === '') lines.pop();

                return lines.map((line, lineIndex) => (
                  <Box
                    key={`${index}-${lineIndex}`}
                    sx={{
                      display: 'flex',
                      bgcolor: part.added
                        ? '#d4edda'
                        : part.removed
                        ? '#f8d7da'
                        : 'transparent',
                    }}
                  >
                    {/* Colored gutter - wide and prominent */}
                    <Box
                      sx={{
                        width: 48,
                        minWidth: 48,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: part.added
                          ? '#28a745'
                          : part.removed
                          ? '#dc3545'
                          : 'grey.100',
                        color: part.added || part.removed ? 'white' : 'text.disabled',
                        fontWeight: 700,
                        fontSize: '1rem',
                        userSelect: 'none',
                        borderRight: '1px solid',
                        borderRightColor: 'divider',
                      }}
                    >
                      {part.added ? '+' : part.removed ? '−' : ''}
                    </Box>
                    {/* Line content */}
                    <Box
                      sx={{
                        flex: 1,
                        px: 2,
                        py: 0.5,
                        color: part.removed ? '#721c24' : part.added ? '#155724' : 'text.primary',
                        textDecoration: part.removed ? 'line-through' : 'none',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {line || ' '}
                    </Box>
                  </Box>
                ));
              })}
            </Box>
          ) : (
            /* Edit View */
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                Make any final edits to the proposed prompt. Changes will be reflected in the diff when you switch back.
              </Typography>
              <textarea
                value={diffPreview?.newText || ''}
                onChange={(e) => handleDiffTextChange(e.target.value)}
                style={{
                  flex: 1,
                  width: '100%',
                  minHeight: 400,
                  padding: '12px',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  lineHeight: 1.6,
                  border: '2px solid #1976d2',
                  borderRadius: '4px',
                  outline: 'none',
                  resize: 'none',
                  backgroundColor: '#fafafa',
                }}
              />
            </Box>
          )}
        </DialogContent>
        <Divider />
        <Box sx={{ p: 1.5, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={handleCancelApply}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={handleConfirmApply}>
            Apply Changes
          </Button>
        </Box>
      </Dialog>

      {/* Test Complete Notification */}
      <Snackbar
        open={showTestComplete}
        autoHideDuration={5000}
        onClose={() => setShowTestComplete(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowTestComplete(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%', fontWeight: 600 }}
        >
          Test completed! Check the Test Result tab to see output.
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
